import { FormControl, FormGroup, Validators } from "@angular/forms";
import { FormTypeEnum } from "src/app/classes/enums";
import {
  IChooseSpecificationsForm,
  ITroubleshootingForm,
  IWindscreenForm
} from "src/app/interfaces/interfaces";
import { PackagesFilterPipe } from "src/app/pipes/packages-filter.pipe";
import { PackagesServiceFilterPipe } from "src/app/pipes/packages-service-filter.pipe";
import { BusinessProposalService } from "src/app/services/business-proposal.service";
import {
  InsuranceCompanyResponse,
  PackageResponse,
  PackageTagEnum,
  PackageTypeEnum,
  VehicleWarningLightsResponse,
  WorkshopSystemEnum
} from "src/domain/client";

const populateFormWithPackages = (
  initFormGrop: FormGroup<any>,
  packages: PackageResponse[],
  disableInputs = false,
  proposalPackages: PackageResponse[] = [],
  existingStateSelections: any = {}
) => {
  const recommended = packages.find((j: PackageResponse) =>
    j.tags.includes(PackageTagEnum.Recommended)
  );
  packages.forEach((pkg) => {
    const tmpForm = new FormGroup({});
    if (!pkg.isSubCategory) {
      pkg.subPackages.forEach((sp) => {
        // Check if this subpackage is selected in proposal, existing state, or is recommended/main
        const isInProposal = proposalPackages[pkg.id]?.[sp.id] === true;
        const isInExistingState =
          existingStateSelections[pkg.id]?.[sp.id] === true;
        const isRecommendedOrMain =
          sp.tags.includes(PackageTagEnum.Recommended) ||
          sp.tags.includes(PackageTagEnum.MainPackage);

        const value = isInProposal || isInExistingState || isRecommendedOrMain;

        tmpForm.addControl(
          sp.id,
          new FormControl({
            value: value,
            disabled:
              disableInputs ||
              (!pkg.tags.includes(PackageTagEnum.Recommended) &&
                pkg.type === PackageTypeEnum.OriginalService)
          })
        );
      });
    } else {
      const proposalSubPackage = pkg.subPackages.find(
        (subPackage) =>
          proposalPackages[pkg.id]?.subcategoryContainer === subPackage.id
      );

      const existingSubPackage = pkg.subPackages.find(
        (subPackage) =>
          existingStateSelections[pkg.id]?.subcategoryContainer ===
          subPackage.id
      );

      const recommendedSubPackage = pkg.subPackages.find((subPackage) =>
        subPackage.tags.includes(PackageTagEnum.Recommended)
      );

      const value =
        proposalSubPackage?.id ||
        existingSubPackage?.id ||
        recommendedSubPackage?.id ||
        "";

      tmpForm.addControl(
        "subcategoryContainer",
        new FormControl({
          value: value,
          disabled: disableInputs
        })
      );
    }
    initFormGrop.addControl(pkg.id, tmpForm);
  });

  const selectedContainer =
    existingStateSelections?.selectedContainer ||
    (recommended ? recommended.id : null);

  initFormGrop.addControl(
    "selectedContainer",
    new FormControl(
      {
        value: selectedContainer,
        disabled: disableInputs
      },
      [Validators.required]
    )
  );
};

const populateAdditionalPackagesForm = (
  formGroup: FormGroup,
  packages: PackageResponse[],
  proposalPackages: PackageResponse[],
  disableInputs = false,
  existingStateSelections: any = {}
) => {
  packages.forEach((p: PackageResponse) => {
    const selected =
      proposalPackages[p.id]?.selected ||
      existingStateSelections[p.id]?.selected ||
      p.tags.includes(PackageTagEnum.Recommended);
    const tmpForm = new FormGroup<any>({});
    tmpForm.addControl(
      "selected",
      new FormControl({
        value: selected,
        disabled:
          (!!proposalPackages[p.id]?.selected ||
            (selected && p.tags.includes(PackageTagEnum.Recommended))) &&
          disableInputs
      })
    );
    if (!p.isSubCategory) {
      p.subPackages.forEach((sp: PackageResponse) => {
        let isChecked = false;

        const selectedPackageFormData = proposalPackages[p.id];
        const existingPackageFormData = existingStateSelections[p.id];

        if (
          selectedPackageFormData &&
          selectedPackageFormData[sp.id] === true
        ) {
          isChecked = true;
        } else if (
          existingPackageFormData &&
          existingPackageFormData[sp.id] === true
        ) {
          isChecked = true;
        }

        tmpForm.addControl(
          sp.id,
          new FormControl({
            value: isChecked,
            disabled:
              (selectedPackageFormData?.[sp.id] === true ||
                (isChecked && proposalPackages[p.id]?.selected)) &&
              disableInputs
          })
        );
      });
    } else {
      const selectedSubPackage = p.subPackages.find(
        (pkg) => proposalPackages[p.id]?.subcategoryContainer === pkg.id
      );

      const existingSubPackage = p.subPackages.find(
        (pkg) => existingStateSelections[p.id]?.subcategoryContainer === pkg.id
      );

      const recommendedSubPackage = p.subPackages.find((pkg) =>
        pkg.tags.includes(PackageTagEnum.Recommended)
      );

      const selectedValue =
        selectedSubPackage?.id ||
        existingSubPackage?.id ||
        recommendedSubPackage?.id;

      tmpForm.addControl(
        "subcategoryContainer",
        new FormControl({
          value: selectedValue || "",
          disabled:
            (!!selectedSubPackage?.id ||
              (selectedValue && proposalPackages[p.id]?.selected)) &&
            disableInputs
        })
      );
    }
    formGroup.addControl(p.id, tmpForm);
  });
};

export function createChooseSpecificationsForm(
  basePackages: PackageResponse[],
  additionalPackages: PackageResponse[],
  serviceTypeSelection: WorkshopSystemEnum[],
  warningLights: VehicleWarningLightsResponse[],
  businessProposalService: BusinessProposalService,
  packageServiceFilterPipe: PackagesServiceFilterPipe,
  packageFilterPipe: PackagesFilterPipe,
  existingStateSelections: any = null
): FormGroup<IChooseSpecificationsForm> {
  const newForm = new FormGroup<IChooseSpecificationsForm>({
    other: new FormControl("")
  });
  const isBusinessProposalActive =
    businessProposalService.isBusinessProposalActive;
  const proposalServices = businessProposalService.proposalServices;
  const propsalPackages = businessProposalService.packages;

  if (serviceTypeSelection.includes(WorkshopSystemEnum.Service)) {
    const servicePackages = packageServiceFilterPipe.transform(basePackages);
    const serviceForm = new FormGroup({});
    const existingServiceForm = existingStateSelections?.serviceForm || {};
    populateFormWithPackages(
      serviceForm,
      servicePackages,
      isBusinessProposalActive && !!proposalServices.Service,
      propsalPackages,
      existingServiceForm
    );
    newForm.addControl(FormTypeEnum.ServiceForm, serviceForm);
  }

  if (additionalPackages && additionalPackages.length > 0) {
    const additionalPackagesForm = new FormGroup({});
    const existingAdditionalPackagesForm =
      existingStateSelections?.additionalPackagesForm || {};
    populateAdditionalPackagesForm(
      additionalPackagesForm,
      additionalPackages,
      propsalPackages,
      isBusinessProposalActive,
      existingAdditionalPackagesForm
    );
    newForm.addControl(FormTypeEnum.AdditionalPackages, additionalPackagesForm);
  }

  if (serviceTypeSelection.includes(WorkshopSystemEnum.Troubleshooting)) {
    const troubleshootingForm = new FormGroup<ITroubleshootingForm>({
      helpText: new FormControl(),
      file: new FormControl(),
      icons: new FormGroup({})
    });
    const troubleshootingPackages = packageFilterPipe.transform(
      basePackages,
      PackageTypeEnum.Troubleshooting
    );
    const existingTroubleshootingForm =
      existingStateSelections?.troubleshootingForm || {};
    populateFormWithPackages(
      troubleshootingForm,
      troubleshootingPackages,
      isBusinessProposalActive && !!proposalServices.Troubleshooting,
      propsalPackages,
      existingTroubleshootingForm
    );
    warningLights.forEach((light) =>
      (troubleshootingForm.controls.icons as FormGroup).addControl(
        light.warningLightId.toString(),
        new FormControl(false)
      )
    );
    newForm.addControl(FormTypeEnum.TroubleshootingForm, troubleshootingForm);
  }

  if (serviceTypeSelection.includes(WorkshopSystemEnum.Windscreen)) {
    const windscreenForm = new FormGroup<IWindscreenForm>({
      insurance: new FormControl(null, [Validators.required]),
      file: new FormControl(),
      date: new FormControl(),
      place: new FormControl(),
      reason: new FormControl(),
      damage: new FormControl()
    });
    const windscreenPackages = packageFilterPipe.transform(
      basePackages,
      PackageTypeEnum.Windshield
    );
    const existingWindscreenForm =
      existingStateSelections?.windscreenForm || {};
    populateFormWithPackages(
      windscreenForm,
      windscreenPackages,
      false,
      [],
      existingWindscreenForm
    );
    newForm.addControl(FormTypeEnum.WindscreenForm, windscreenForm);
  }

  if (serviceTypeSelection.includes(WorkshopSystemEnum.Wheel)) {
    const wheelForm = new FormGroup({});
    const wheelPackages = packageFilterPipe.transform(
      basePackages,
      PackageTypeEnum.Wheel
    );
    const existingWheelForm = existingStateSelections?.wheelForm || {};
    populateFormWithPackages(
      wheelForm,
      wheelPackages,
      isBusinessProposalActive && !!proposalServices.Wheel,
      propsalPackages,
      existingWheelForm
    );
    newForm.addControl(FormTypeEnum.WheelForm, wheelForm);
  }

  if (serviceTypeSelection.includes(WorkshopSystemEnum.TechUpdate)) {
    const techUpdateForm = new FormGroup({});
    const techUpdatePackages = packageFilterPipe.transform(
      basePackages,
      PackageTypeEnum.TechUpdate
    );
    const existingTechUpdateForm =
      existingStateSelections?.techUpdateForm || {};
    populateFormWithPackages(
      techUpdateForm,
      techUpdatePackages,
      isBusinessProposalActive && !!proposalServices.TechUpdate,
      propsalPackages,
      existingTechUpdateForm
    );
    newForm.addControl(FormTypeEnum.TechUpdateForm, techUpdateForm);
  }

  const otherIsRequired = serviceTypeSelection.includes(
    WorkshopSystemEnum.Other
  );
  if (otherIsRequired) {
    newForm
      .get("other")
      ?.setValidators([Validators.required, Validators.minLength(5)]);
  }

  return newForm;
}

export function createExtraServicesForm(
  additionalPackages: PackageResponse[]
): FormGroup {
  const newForm = new FormGroup({});

  if (!additionalPackages) {
    return newForm;
  }

  additionalPackages.forEach((p: PackageResponse) => {
    const packageForm = new FormGroup({});

    packageForm.addControl(
      "selected",
      new FormControl(p.tags.includes(PackageTagEnum.Recommended))
    );

    if (!p.isSubCategory) {
      p.subPackages.forEach((sp: PackageResponse) => {
        packageForm.addControl(
          sp.id,
          new FormControl({ value: false, disabled: false })
        );
      });
    } else {
      const recommendedSubPackage = p.subPackages.find((pkg) =>
        pkg.tags.includes(PackageTagEnum.Recommended)
      );
      packageForm.addControl(
        "subcategoryContainer",
        new FormControl({
          value: recommendedSubPackage?.id || "",
          disabled: false
        })
      );
    }
    newForm.addControl(p.id, packageForm);
  });

  return newForm;
}

export function updateWorkshopSelectionForm(
  form: FormGroup,
  selectedSystems: WorkshopSystemEnum[] | null
): void {
  if (!selectedSystems) {
    form.reset({}, { emitEvent: false });
    return;
  }

  const formValueToSet: { [key: string]: boolean } = {};
  Object.keys(form.controls).forEach((key) => {
    formValueToSet[key] = selectedSystems.includes(key as WorkshopSystemEnum);
  });

  form.patchValue(formValueToSet, { emitEvent: false });
}
